import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Users, Shield, User, Briefcase,
  CheckCircle2, XCircle, ChevronDown,
  Trash2, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import DashboardLayout from '../components/DashboardLayout';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';

// ── Constants ──────────────────────────────────────────────

const ROLES = [
  { value: 'resident', label: 'Residente',    icon: User,      color: 'bg-blue-100 text-blue-700'   },
  { value: 'staff',    label: 'Vigilante',    icon: Briefcase, color: 'bg-amber-100 text-amber-700' },
  { value: 'admin',   label: 'Administrador', icon: Shield,    color: 'bg-purple-100 text-purple-700'},
];

const getRoleConfig = (role) => ROLES.find(r => r.value === role) || ROLES[0];

const RoleBadge = ({ role }) => {
  const cfg = getRoleConfig(role);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <cfg.icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const StatusBadge = ({ isActive }) => (
  isActive !== false
    ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Activo
      </span>
    : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
        <XCircle className="h-3 w-3" /> Inactivo
      </span>
);

const Avatar = ({ name, isActive }) => (
  <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-opacity ${isActive === false ? 'opacity-40' : ''} bg-accent/15 text-accent`}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Main Page ──────────────────────────────────────────────

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [roleFilter, setRoleFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null); // user object
  const [deleting, setDeleting]       = useState(false);
  const [updatingId, setUpdatingId]   = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)                    params.search = search;
      if (roleFilter !== 'all')      params.role   = roleFilter;
      if (statusFilter !== 'all')    params.status = statusFilter;
      const { data } = await axios.get(`${API}/admin/users`, { params });
      setUsers(data);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { fetchUsers(); }, 300);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingId(userId);
    try {
      const { data } = await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: data.role } : u));
      toast.success(`Rol actualizado a "${getRoleConfig(newRole).label}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar rol');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    setUpdatingId(userId);
    const newStatus = currentStatus === false ? true : false;
    try {
      const { data } = await axios.patch(`${API}/admin/users/${userId}/status`, { is_active: newStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: data.is_active } : u));
      toast.success(newStatus ? 'Cuenta activada' : 'Cuenta desactivada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`);
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      toast.success(`Usuario "${deleteTarget.name}" eliminado`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleting(false);
    }
  };

  const activeCount   = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;

  return (
    <DashboardLayout>
      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary">Gestión de Usuarios</h1>
          <p className="text-text-secondary mt-1">Administra roles, estados y accesos de todos los usuarios registrados.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Users className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold text-slate-700">{users.length} usuarios</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchUsers}
            className="gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: users.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Activos',   value: activeCount,   color: 'text-emerald-700', bg: 'bg-emerald-50' },
          { label: 'Inactivos', value: inactiveCount, color: 'text-slate-500',   bg: 'bg-slate-50'   },
          { label: 'Admins',    value: users.filter(u => u.role === 'admin').length, color: 'text-purple-700', bg: 'bg-purple-50' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-2xl p-4 border border-slate-100`}>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
            <p className={`text-2xl font-heading font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-50 border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-44 bg-slate-50 border-slate-200" data-testid="role-filter">
              <SelectValue placeholder="Filtrar por rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 bg-slate-50 border-slate-200" data-testid="status-filter">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-20">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No se encontraron usuarios</p>
            <p className="text-sm text-slate-400 mt-1">Ajusta los filtros de búsqueda</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/70">
                    {['Usuario', 'Rol', 'Estado', 'Registro', 'Acciones'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-6 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      data-testid={`user-row-${u.id}`}
                      className={`transition-colors hover:bg-slate-50/80 ${u.is_active === false ? 'opacity-60' : ''}`}
                    >
                      {/* Usuario */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} isActive={u.is_active} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate max-w-[180px]">{u.name || '—'}</p>
                            <p className="text-xs text-slate-400 truncate max-w-[180px]">{u.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Rol */}
                      <td className="px-6 py-4">
                        <Select
                          value={u.role}
                          onValueChange={(v) => handleRoleChange(u.id, v)}
                          disabled={u.id === currentUser?.id || updatingId === u.id}
                        >
                          <SelectTrigger
                            className="h-8 w-40 border-slate-200 bg-transparent text-xs gap-1 focus:ring-1 focus:ring-accent"
                            data-testid={`role-select-${u.id}`}
                          >
                            <RoleBadge role={u.role} />
                            <ChevronDown className="h-3 w-3 text-slate-400 ml-auto shrink-0" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map(r => (
                              <SelectItem key={r.value} value={r.value}>
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${r.color}`}>
                                  <r.icon className="h-3 w-3" /> {r.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4">
                        <StatusBadge isActive={u.is_active} />
                      </td>

                      {/* Fecha registro */}
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-500">{formatDate(u.created_at)}</span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {u.id !== currentUser?.id && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleStatusToggle(u.id, u.is_active)}
                                disabled={updatingId === u.id}
                                className="h-8 text-xs gap-1.5 border-slate-200 hover:border-accent hover:text-accent"
                                data-testid={`toggle-status-${u.id}`}
                              >
                                {u.is_active === false
                                  ? <><CheckCircle2 className="h-3.5 w-3.5" /> Activar</>
                                  : <><XCircle className="h-3.5 w-3.5" /> Desactivar</>
                                }
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteTarget(u)}
                                className="h-8 text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                                data-testid={`delete-btn-${u.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Eliminar
                              </Button>
                            </>
                          )}
                          {u.id === currentUser?.id && (
                            <span className="text-xs text-slate-400 italic">Tu cuenta</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {users.map((u) => (
                <div
                  key={u.id}
                  className={`p-4 space-y-3 ${u.is_active === false ? 'opacity-60' : ''}`}
                  data-testid={`user-card-${u.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar name={u.name} isActive={u.is_active} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{u.name || '—'}</p>
                        <p className="text-xs text-slate-400 truncate">{u.email}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDate(u.created_at)}</p>
                      </div>
                    </div>
                    <StatusBadge isActive={u.is_active} />
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Select
                      value={u.role}
                      onValueChange={(v) => handleRoleChange(u.id, v)}
                      disabled={u.id === currentUser?.id || updatingId === u.id}
                    >
                      <SelectTrigger className="h-8 w-38 border-slate-200 bg-slate-50 text-xs">
                        <RoleBadge role={u.role} />
                        <ChevronDown className="h-3 w-3 text-slate-400 ml-auto shrink-0" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map(r => (
                          <SelectItem key={r.value} value={r.value}>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${r.color}`}>
                              <r.icon className="h-3 w-3" /> {r.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {u.id !== currentUser?.id && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusToggle(u.id, u.is_active)}
                          disabled={updatingId === u.id}
                          className="h-8 text-xs gap-1 border-slate-200"
                          data-testid={`toggle-status-mobile-${u.id}`}
                        >
                          {u.is_active === false ? 'Activar' : 'Desactivar'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteTarget(u)}
                          className="h-8 text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`delete-btn-mobile-${u.id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Eliminar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer count */}
            <div className="px-6 py-3 border-t border-slate-50 bg-slate-50/50">
              <p className="text-xs text-slate-400">
                Mostrando {users.length} usuario{users.length !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Delete Confirmation Modal ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading font-semibold text-slate-900">
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="flex items-center gap-4 p-4 bg-red-50 rounded-xl mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">{deleteTarget?.name}</p>
                <p className="text-sm text-slate-500">{deleteTarget?.email}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              ¿Estás seguro de que deseas eliminar este usuario? Esta acción es{' '}
              <span className="font-semibold text-red-600">irreversible</span> y eliminará todos sus datos asociados.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              data-testid="cancel-delete-btn"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <div className="animate-spin h-4 w-4 border-b-2 border-white rounded-full" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? 'Eliminando...' : 'Sí, eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
